// src/components/ui/lists/RequestsManagementView/RequestsManagementViewShadcn.jsx
import React, { useState } from 'react';
import {
  Inbox,
  Send,
  Clock,
  Users,
  Search,
  Filter,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { Input } from '../../input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../tabs';
import { Badge } from '../../badge';

// Import shadcn partner request card (will create this)
import PartnerRequestCardShadcn from '../../cards/PartnerRequestCard/PartnerRequestCardShadcn';

import { cn } from '../../../../utils/cn';

const RequestsManagementViewShadcn = ({
  pendingConnections = {},
  onAcceptRequest,
  onRejectRequest,
  onCancelRequest,
  loading = false,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Normalize pending connections data
  const receivedRequests = pendingConnections.received || [];
  const sentRequests = pendingConnections.sent || [];

  // Filter requests based on search
  const filteredReceivedRequests = React.useMemo(() => {
    if (!searchTerm.trim()) return receivedRequests;

    const search = searchTerm.toLowerCase();
    return receivedRequests.filter(request => {
      const businessName = request.requesting_business?.name || request.business_name || '';
      const businessEmail = request.requesting_business?.contact_email || request.contact_email || '';
      const notes = request.notes || '';

      return businessName.toLowerCase().includes(search) ||
             businessEmail.toLowerCase().includes(search) ||
             notes.toLowerCase().includes(search);
    });
  }, [receivedRequests, searchTerm]);

  const filteredSentRequests = React.useMemo(() => {
    if (!searchTerm.trim()) return sentRequests;

    const search = searchTerm.toLowerCase();
    return sentRequests.filter(request => {
      const businessName = request.target_business?.name || request.business_name || '';
      const businessEmail = request.target_business?.contact_email || request.contact_email || '';
      const notes = request.notes || '';

      return businessName.toLowerCase().includes(search) ||
             businessEmail.toLowerCase().includes(search) ||
             notes.toLowerCase().includes(search);
    });
  }, [sentRequests, searchTerm]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (receivedRequests.length === 0 && sentRequests.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center py-12">
          <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Pending Requests</h3>
          <p className="text-muted-foreground">
            You have no pending connection requests at this time.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="flex items-center gap-2">
                <Inbox className="w-5 h-5" />
                Connection Requests
              </CardTitle>
              <Badge variant="secondary">
                {receivedRequests.length + sentRequests.length} Total
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Requests Tabs */}
      <Tabs defaultValue="received" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="received" className="flex items-center gap-2">
            <Inbox className="w-4 h-4" />
            Received
            {receivedRequests.length > 0 && (
              <Badge variant="secondary">{receivedRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Sent
            {sentRequests.length > 0 && (
              <Badge variant="secondary">{sentRequests.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="space-y-4">
          {filteredReceivedRequests.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center py-12">
                <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm ? 'No matching requests' : 'No received requests'}
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm
                    ? 'Try adjusting your search criteria.'
                    : 'When other businesses send you connection requests, they will appear here.'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredReceivedRequests.map((request) => (
                <PartnerRequestCardShadcn
                  key={request.id}
                  request={request}
                  type="received"
                  onAccept={() => onAcceptRequest && onAcceptRequest(request.id, request.requesting_business?.name || 'Business')}
                  onReject={() => onRejectRequest && onRejectRequest(request.id, request.requesting_business?.name || 'Business')}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent" className="space-y-4">
          {filteredSentRequests.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center py-12">
                <Send className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm ? 'No matching requests' : 'No sent requests'}
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm
                    ? 'Try adjusting your search criteria.'
                    : 'Connection requests you send to other businesses will appear here.'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredSentRequests.map((request) => (
                <PartnerRequestCardShadcn
                  key={request.id}
                  request={request}
                  type="sent"
                  onCancel={() => onCancelRequest && onCancelRequest(request.id, request.target_business?.name || 'Business')}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RequestsManagementViewShadcn;